import { Injectable, inject } from "@angular/core";
import _, { cloneDeep, merge } from "lodash";
import { BehaviorSubject, Subject } from "rxjs";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { CommandService } from "~app/common/services/cqs/command.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { DeleteSnabbkalkylmallCommand } from "../commands/tradslag/DeleteSnabbkalkylmallCommand";
import { UpdateDirektutbyteCommand } from "../commands/tradslag/UpdateDirektutbyteCommand";
import { UpdateSnabbkalkylCommand } from "../commands/tradslag/UpdateSnabbkalkylCommand";
import { Geometri } from "../models/karta-models";
import { VKDirektutbyte, VKDirektutbyteArborealDTO, VKDirektutbyteDTO, VKDirektutbyteTradslagDTO } from "../models/tradslag/direktutbyte-models";
import { VKSnabbkalkyl, VKSnabbkalkylDTO } from "../models/tradslag/snabbkalkyl-models";
import { VKKalkylmallTradslagDTO } from "../models/tradslag/snabbkalkylmall-models";
import { TradslagKalkylMetod as TradslagKalkylmetod } from "../models/tradslag/tradslag-models";
import { AvtalService } from "./avtal.service";
import { KontraktCommandService } from "./kontrakt-command.service";

@Injectable()
export class TradslagMediatorService {
    readonly onMallChanged = new Subject<void>();
    readonly validationNeeded$ = new Subject();

    private readonly avtalService = inject(AvtalService);
    private readonly commandService = inject(CommandService);
    private readonly grundytaForeSrc = new Subject<number>();
    private readonly kontraktCommandService = inject(KontraktCommandService, { optional: true });
    private readonly loadedSrc = new BehaviorSubject<boolean>(false);
    private readonly medelstamSrc = new Subject<number>();
    private readonly onDirektutbyteCalculatedSubject$ = new BehaviorSubject<VKDirektutbyte>(null);
    private readonly onDirektutbyteTradslagImported = new Subject<VKDirektutbyte>();
    private readonly onKalkylMetodChanged = new Subject<void>();
    private readonly onMallSelected = new Subject<[VKSnabbkalkyl, VKDirektutbyte]>();
    private readonly onSaveMall = new Subject<string>();
    private readonly onSnabbkalkylCalculatedSubject$ = new BehaviorSubject<VKSnabbkalkyl>(null);
    private readonly raskModalService = inject(RaskModalService);

    private direktutbyteModelSrc: VKDirektutbyte;
    private importedUtbyteTradslagModelSrc: VKDirektutbyteTradslagDTO[];
    private kalkylmetodSrc: TradslagKalkylmetod = "snabbkalkyl";
    private snabbkalkylModelSrc: VKSnabbkalkyl;

    get calculatedGrundytaFore$() { return this.grundytaForeSrc.asObservable(); }

    get calculatedMedelstam$() { return this.medelstamSrc.asObservable(); }

    get direktutbyte() { return this.direktutbyteModelSrc; }

    get importedUtbyteTradslag() { return this.importedUtbyteTradslagModelSrc; }

    get kalkylmetod() {
        return this.kalkylmetodSrc;
    }

    get loaded() {
        return this.loadedSrc.value;
    }

    get loaded$() { return this.loadedSrc.asObservable(); }

    get onDirektutbyteCalculated$() { return this.onDirektutbyteCalculatedSubject$.asObservable(); }

    get onDirektutbyteTradslagImported$() { return this.onDirektutbyteTradslagImported.asObservable(); }

    get onKalkylMetodChanged$() { return this.onKalkylMetodChanged.asObservable(); }

    get onMallChanged$() { return this.onMallChanged.asObservable(); }

    get onMallSelected$() { return this.onMallSelected.asObservable(); }

    get onSaveMall$() { return this.onSaveMall.asObservable(); }

    get onSnabbkalkylCalculated$() { return this.onSnabbkalkylCalculatedSubject$.asObservable(); }

    get snabbkalkyl() { return this.snabbkalkylModelSrc; }

    changeKalkylmetod(value: TradslagKalkylmetod) {
        this.kalkylmetodSrc = value;
        this.onKalkylMetodChanged.next();
    }

    deleteMall(mallItem: SimpleSelectionItem): void {
        const message = `Är du säker på att du vill ta bort mallen (${mallItem.text})?`;

        this.raskModalService.confirm(new ModalSettings("Ta bort Mall?",message))
            .subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    const command = new DeleteSnabbkalkylmallCommand(mallItem.value);

                    this.commandService.addTransactionCommand(command);
                    const obs = this.commandService.pushCommandQueueTransaction();
                    obs.subscribe(x => {
                        this.commandService.reset();
                        this.onMallChanged.next();
                    });
                }
            });
    }

    async init(getSnabbkalkyl: Promise<VKSnabbkalkylDTO>, getDirektutbyte: Promise<VKDirektutbyteDTO>) {
        const [
            snabbkalkyl,
            direktutbyte
        ] = await Promise.all([
            getSnabbkalkyl,
            getDirektutbyte
        ]);

        if (ObjectUtils.isNullOrUndefined(snabbkalkyl) && ObjectUtils.hasValue(direktutbyte)) {
            this.changeKalkylmetod("direktutbyte");
        }

        this.snabbkalkylModelSrc = new VKSnabbkalkyl(snabbkalkyl);
        this.direktutbyteModelSrc = new VKDirektutbyte(direktutbyte);
        this.loadedSrc.next(true);
    }

    async loadMall(mallId: number) {
        this.avtalService.getSnabbKalkylmall(mallId).subscribe(snabbkalkylmall => {
            const snabbkalkyl: VKSnabbkalkylDTO = new VKSnabbkalkylDTO();
            const direktutbyte: VKDirektutbyteDTO = new VKDirektutbyteDTO();
            snabbkalkyl.snabbkalkylTradslag = [];
            direktutbyte.direktutbyteTradslag = [];
            snabbkalkyl.uttagM3FubHa = this.snabbkalkyl?.uttagM3FubHa;

            snabbkalkylmall
                .kalkylmallTradslag?.forEach((kalkylmallTradslag: VKKalkylmallTradslagDTO, i) => {
                    const tradslag = kalkylmallTradslag.tradslag;

                    snabbkalkyl.snabbkalkylTradslag.push({
                        andel: kalkylmallTradslag.andel,
                        medelstam: kalkylmallTradslag.medelstam,
                        timmerandel: kalkylmallTradslag.timmerandel,
                        massavedandel: kalkylmallTradslag.massavedandel,
                        granmassavedsandel: kalkylmallTradslag.granmassavedsandel,
                        branslevedandel: kalkylmallTradslag.branslevedandel,
                        varavGrovtimmer: kalkylmallTradslag.varavGrovtimmer,
                        varavKlentimmer: kalkylmallTradslag.varavKlentimmer,
                        tradslag
                    });

                    direktutbyte.direktutbyteTradslag.push({
                        tradslag
                    });
                });

            let snabbkalkylModel: VKSnabbkalkyl;
            let direktutbyteModel: VKDirektutbyte;

            if (this.kalkylmetodSrc === "snabbkalkyl") {
                snabbkalkylModel = new VKSnabbkalkyl(snabbkalkyl);
                this.snabbkalkylModelSrc = snabbkalkylModel;
            } else if (this.kalkylmetodSrc === "direktutbyte") {
                direktutbyteModel = new VKDirektutbyte(direktutbyte);
                this.direktutbyteModelSrc = direktutbyteModel;
            }

            this.onMallSelected.next([
                snabbkalkylModel,
                direktutbyteModel
            ]);
        });
    }

    mapDirektutbyteFromArboreal(direktUtbyteArborealResponse: VKDirektutbyteArborealDTO, guid: string): void {
        const importedUtbyteTradslag = direktUtbyteArborealResponse?.direktutbyteTradslag;
        const currentDirektutbyte = this.direktutbyteModelSrc ?? new VKDirektutbyte();
        this.importedUtbyteTradslagModelSrc = importedUtbyteTradslag ? cloneDeep(importedUtbyteTradslag) : [] as VKDirektutbyteTradslagDTO[];

        if (direktUtbyteArborealResponse && importedUtbyteTradslag?.length > 0) {
            currentDirektutbyte.arborealBeraknadTid = direktUtbyteArborealResponse.arborealBeraknadTid;

            this.mapImportedTradslagToCurrentDirektutbyte(importedUtbyteTradslag, currentDirektutbyte);
            this.updateDirektutbyte(guid, currentDirektutbyte, false);
            this.onDirektutbyteTradslagImported.next(currentDirektutbyte);
        }
    }

    mapDirektutbyteFromImport(geometri: Geometri, guid: string): void {
        if (geometri?.atgardGuid !== guid) {
            return;
        }

        const kartgeometri = geometri?.kartgeometri;
        const importedUtbyteTradslag = kartgeometri?.direktutbyteTradslag;
        const currentDirektutbyte = this.direktutbyteModelSrc ?? new VKDirektutbyte();
        this.importedUtbyteTradslagModelSrc = importedUtbyteTradslag ? cloneDeep(importedUtbyteTradslag) : [] as VKDirektutbyteTradslagDTO[];

        if (kartgeometri && importedUtbyteTradslag?.length > 0) {
            currentDirektutbyte.importKalla = kartgeometri.importkalla;

            this.mapImportedTradslagToCurrentDirektutbyte(importedUtbyteTradslag, currentDirektutbyte);
            this.updateDirektutbyte(guid, currentDirektutbyte, false);
            this.onDirektutbyteTradslagImported.next(currentDirektutbyte);
        }
    }

    recalculateGrundytaFore() {
        const direktutbyteTradslag = this.direktutbyte.direktutbyteTradslag;
        const grundytaFore = direktutbyteTradslag.reduce((sum, tradslag) => sum + (_.isNil(tradslag.grundyta) ? 0 : tradslag.grundyta), 0);

        if (!_.isNil(grundytaFore)) {
            this.grundytaForeSrc.next(grundytaFore);
        } else {
            return;
        }
    }

    recalculateMedelstam() {
        const medelstamSnabbkalkyl = this.snabbkalkyl.medelstam;
        const medelstamDirektutbyte = this.direktutbyte.medelstam;

        //Finns det en summa på snabbkalkyl så används den först.
        let medelstam: number;
        if (!_.isNil(medelstamSnabbkalkyl)) {
            medelstam = medelstamSnabbkalkyl;
        } else if (!_.isNil(medelstamDirektutbyte)) {
            medelstam = medelstamDirektutbyte;
        } else {
            return;
        }

        this.medelstamSrc.next(medelstam);
    }

    removeImportedDirektutbyteInMemory(): void {
        if (this.importedUtbyteTradslagModelSrc) {
            this.importedUtbyteTradslagModelSrc = null;
        }
    }

    saveMall(mallNamn: string) {
        this.onSaveMall.next(mallNamn);
    }

    updateDirektutbyte(atgardGuid: string, model: VKDirektutbyte, isCalculate = false): void {
        const command = new UpdateDirektutbyteCommand(atgardGuid, model);
        this.kontraktCommandService.update(command);
        this.direktutbyteModelSrc = merge(this.direktutbyteModelSrc, model);
        if (isCalculate) {
            this.onDirektutbyteCalculatedSubject$.next(model);
        }
    }

    updateDirektutbyteLegacy(model: VKDirektutbyte): void {
        this.direktutbyteModelSrc = merge(this.direktutbyteModelSrc, model);
    }

    updateSnabbkalkyl(atgardGuid: string, model: VKSnabbkalkyl, isCalculate = false): void {
        const command = new UpdateSnabbkalkylCommand(atgardGuid, model);
        this.kontraktCommandService.update(command);
        this.snabbkalkylModelSrc = merge(this.snabbkalkylModelSrc, model);
        if (isCalculate) {
            this.onSnabbkalkylCalculatedSubject$.next(model);
        }
    }

    updateSnabbkalkylLegacy(model: VKSnabbkalkyl): void {
        this.snabbkalkylModelSrc = merge(this.snabbkalkylModelSrc, model);
    }

    private mapImportedTradslagToCurrentDirektutbyte(importedUtbyteTradslag: VKDirektutbyteTradslagDTO[], currentDirektutbyte: VKDirektutbyte) {
        importedUtbyteTradslag.forEach(imported => {
            const currentTradslag = currentDirektutbyte?.direktutbyteTradslag.find(t => t.tradslag.id === imported.tradslag.id);
            if (!_.isNil(currentTradslag)) {
                currentTradslag.diameterGrundytevagd = imported?.diameterGrundytevagd ?? currentTradslag.diameterGrundytevagd;
                currentTradslag.grundyta = imported?.grundyta ?? currentTradslag.grundyta;
                currentTradslag.hojdGrundyteVagd = imported?.hojdGrundyteVagd ?? currentTradslag.hojdGrundyteVagd;
                currentTradslag.m3skHa = imported?.m3skHa ?? currentTradslag.m3skHa;
                currentTradslag.medelstam = imported?.medelstam ?? currentTradslag.medelstam;
                currentTradslag.stammarperHa = imported?.stammarperHa ?? currentTradslag.stammarperHa;
            } else {
                let newTradslag = new VKDirektutbyteTradslagDTO();
                newTradslag = merge(newTradslag, imported);
                currentDirektutbyte.direktutbyteTradslag.push(newTradslag);
            }
        });
    }
}
